import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import type { EsignatureSignature } from 'modules/esignatures/hooks/signatures/useGetEsignatureSignatures'
import { useGenerateAuditTrailEsignatureSignature } from 'modules/esignatures/hooks/signatures/useGenerateAuditTrailEsignatureSignature'

import AuditTrailContent from './Content'

interface Props {
  signature: EsignatureSignature
  onClose: () => void
}

const getDownloadAuditTrailUrl = (id: number) => {
  const queryParams = {
    id: id.toString(),
  }

  return `/esignature/signatures/download_audit_trail?${new URLSearchParams(
    queryParams
  ).toString()}`
}

const AUDIT_TRAIL_GENERATION_DELAY_MS = 10_000

const AuditTrailModal = ({ signature, onClose }: Props) => {
  const audit_trail_url = getDownloadAuditTrailUrl(signature.id)
  const { generateAuditTrail } = useGenerateAuditTrailEsignatureSignature()
  const [isGenerating, setIsGenerating] = React.useState(false)

  const { performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      app.setProgress({
        message: i18n.t('common.loading'),
      })

      try {
        const result = await generateAuditTrail({ id: signature.id })
        const handled = handleResult(result, () => undefined)

        if (!handled.success) {
          throw new Error('Failed to generate audit trail')
        }

        setIsGenerating(true)
        await new Promise((resolve) => setTimeout(resolve, AUDIT_TRAIL_GENERATION_DELAY_MS))

        const api = ApiClient()
        const downloadResult = await api.get(audit_trail_url)
        setIsGenerating(false)
        if (downloadResult) {
          window.location.href = buildUrl(audit_trail_url)
        }
      } catch (error) {
        setIsGenerating(false)
        Sentry.captureException(error)
        app.setFlash({
          type: 'wrong',
          message: i18n.t('documents.modal.audit_trail.modal.download_error'),
        })
      }

      app.setProgress(null)
      closePortal()
    },
    {
      successFlash: false,
    }
  )

  return (
    <Modal
      illustration="esignature"
      title={i18n.t('documents.modal.audit_trail.modal.title')}
      description={<AuditTrailContent />}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('documents.modal.audit_trail.modal.cancel')}
          </Button>
          <Button submit iconName="download" disabled={isGenerating} loading={isGenerating}>
            {i18n.t('documents.modal.audit_trail.modal.download')}
          </Button>
        </>
      }
    ></Modal>
  )
}

export default AuditTrailModal
